// Who an address's invoices go to (customers.setBillTo): another customer, found by name, number or
// street, or the address's own customer again. Invoices already made keep whom they bill; a site
// contact who was the old bill-to's is cleared, and Save says so.

import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import type { Customer } from '../types.ts';
import { Sheet, SheetButtons, useWrite } from '../shared/EditSheet.tsx';
import { billToSaid, clearsSiteContact, placeName } from './bill-to-model.ts';

type Loc = Customer['locations'][number];
type Found = { id: string; displayName: string; city: string | null };

export function EditBillTo({
  customer,
  location,
  onSaved,
  onClose,
  onLatest,
}: {
  customer: Customer;
  location: Loc;
  onSaved: (c: Customer, said: string) => void;
  onClose: () => void;
  onLatest: () => Promise<unknown>;
}) {
  const was = location.billTo ?? null;
  // undefined: not chosen yet; null: the customer themselves
  const [to, setTo] = useState<Found | null | undefined>(undefined);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<Found[]>([]);
  const w = useWrite(JSON.stringify(to ?? null));
  useEffect(() => {
    if (!query.trim()) return setFound([]);
    const t = setTimeout(() => {
      client
        .call<{ customers: Found[] }>('customers.list', { query: query.trim(), limit: 8 })
        .then((r) => setFound(r.customers.filter((c) => c.id !== customer.id && c.id !== was?.customerId)))
        .catch(() => setFound([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query, customer.id, was?.customerId]);
  const place = placeName(location);
  const save = async () => {
    if (to === undefined) return;
    const r = await w.run<{ customer: Customer; siteContactCleared: { name: string } | null }>(
      'customers.setBillTo',
      {
        input: { locationId: location.id, billToCustomerId: to?.id ?? null, rowVersion: location.rowVersion },
      },
    );
    if (r)
      onSaved(
        r.customer,
        billToSaid({
          place,
          customerName: customer.displayName,
          to: to?.displayName ?? null,
          was: was?.name ?? null,
          cleared: r.siteContactCleared,
        }),
      );
  };
  const site = location.siteContact;
  return (
    <Sheet title={`Who ${place} bills to`} onClose={onClose}>
      <p className="small">
        Now: <b>{was ? was.name : `${customer.displayName} (the customer)`}</b>
      </p>
      {to === undefined ? (
        <>
          <label className="f">
            Bill another customer
            <input
              className="field"
              type="search"
              placeholder="Name, number or street"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </label>
          {found.length > 0 && (
            <ul className="found" aria-label="Customers found">
              {found.map((c) => (
                <li key={c.id}>
                  <button type="button" className="row-btn" onClick={() => setTo(c)}>
                    <b>{c.displayName}</b>
                    {c.city && <span className="muted small"> · {c.city}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {was && (
            <button type="button" className="btn soft" onClick={() => setTo(null)}>
              Bill {customer.displayName} instead
            </button>
          )}
        </>
      ) : (
        <p className="small" role="status">
          {to ? (
            <>
              Invoices for {place} will go to <b>{to.displayName}</b>.
            </>
          ) : (
            <>
              Invoices for {place} will go to <b>{customer.displayName}</b> again.
            </>
          )}{' '}
          <button type="button" className="btn quiet sm" onClick={() => setTo(undefined)}>
            Pick another
          </button>
        </p>
      )}
      {to !== undefined && was && site?.of === 'bill_to' && (
        <p className="notice warn small">{clearsSiteContact(site.name, was.name)}</p>
      )}
      <p className="muted small">Invoices already made keep whom they bill.</p>
      <SheetButtons
        w={w}
        label="Save"
        busyLabel="Saving…"
        disabled={to === undefined}
        onSave={() => void save()}
        onCancel={onClose}
        onLatest={onLatest}
      />
    </Sheet>
  );
}
